import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, MessageCircle, Menu, X, LifeBuoy } from "lucide-react";
import { SITE, NAV } from "../lib/site";
import { ContactMenu } from "./ContactMenu";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "bg-black/80 backdrop-blur-xl border-[#232323]"
          : "bg-black/40 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="hidden md:flex items-center justify-between border-b border-[#232323]/60 px-5 md:px-8 h-9 bg-black/40">
        <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#8a8a8a]">
          <span className="h-1.5 w-1.5 animate-pulse bg-[#ff3300]" />
          Attivi h24 · {SITE.area}
        </span>
        <div className="flex items-center gap-5">
          <a
            href={`tel:${SITE.phoneTel}`}
            data-testid="topbar-phone-1"
            className="flex items-center gap-2 font-mono text-[11px] tracking-wide text-[#f4f4f0] transition-colors duration-150 hover:text-[#ff3300]"
          >
            <Phone size={12} className="text-[#ff3300]" /> {SITE.phoneDisplay}
          </a>
          <span className="h-3 w-px bg-[#232323]" />
          <a
            href={`tel:${SITE.phone2Tel}`}
            data-testid="topbar-phone-2"
            className="flex items-center gap-2 font-mono text-[11px] tracking-wide text-[#f4f4f0] transition-colors duration-150 hover:text-[#ff3300]"
          >
            <Phone size={12} className="text-[#ff3300]" /> {SITE.phone2Display}
          </a>
          <a
            href={SITE.whatsapp2Msg}
            target="_blank"
            rel="noreferrer"
            data-testid="topbar-whatsapp"
            className="flex items-center gap-2 font-mono text-[11px] tracking-wide text-[#25d366] transition-colors duration-150 hover:text-[#f4f4f0]"
          >
            <MessageCircle size={12} /> WhatsApp
          </a>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 md:px-8 h-[68px] flex items-center justify-between">
        <Link
          to="/"
          data-testid="logo-link"
          className="group flex items-center"
        >
          <img
            src="/logo.jpeg"
            alt="Rottamazioni Roma — Ritiro auto, demolizione, pratiche gratis"
            className="h-11 md:h-12 w-auto rounded-md transition-transform duration-200 group-hover:scale-[1.03]"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`font-mono text-xs uppercase tracking-[0.18em] transition-colors duration-150 hover:text-[#ff3300] ${
                pathname === n.to ? "text-[#f4f4f0]" : "text-[#8a8a8a]"
              }`}
            >
              {n.label}
            </Link>
          ))}
          <a
            href={SITE.soccorso}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-soccorso"
            className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.18em] text-[#ff3300] transition-colors duration-150 hover:text-[#f4f4f0]"
          >
            <LifeBuoy size={13} /> Soccorso H24
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <ContactMenu
            type="call"
            label="Chiama Ora"
            testid="header-call"
            wrapperClassName="hidden sm:block"
            className="flex items-center gap-2 border border-[#ff3300] bg-[#ff3300] px-4 py-2 font-mono text-xs uppercase tracking-[0.14em] text-black transition-colors duration-150 hover:bg-transparent hover:text-[#ff3300]"
          />
          <button
            data-testid="menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden flex h-10 w-10 items-center justify-center border border-[#232323]"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-[#232323] bg-black"
        >
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className="block border-b border-[#232323] px-5 py-4 font-display text-2xl tracking-wide"
            >
              {n.label}
            </Link>
          ))}
          <a
            href={SITE.soccorso}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-nav-soccorso"
            className="flex items-center gap-2 border-b border-[#232323] px-5 py-4 font-display text-2xl tracking-wide text-[#ff3300]"
          >
            <LifeBuoy size={20} /> Soccorso H24
          </a>
          <a
            href={`tel:${SITE.phoneTel}`}
            className="block bg-[#ff3300] px-5 py-4 font-display text-2xl tracking-wide text-black"
          >
            {SITE.phoneDisplay}
          </a>
          <a
            href={`tel:${SITE.phone2Tel}`}
            className="block border-t border-black/20 bg-[#ff3300] px-5 py-4 font-display text-2xl tracking-wide text-black"
          >
            {SITE.phone2Display}
          </a>
        </div>
      )}
    </header>
  );
};
